/**
 * Personnel — GET /api/v1/control/personnel[/{record_id}], tenant-scoped by
 * X-Company-Id.
 *
 * The list deliberately omits avatar_base64 (hydrating every face is expensive), so
 * fetchPersonnel(record_id) is the only call that returns an avatar.
 */
import {requestControl, requestControlRaw} from '@/shared/api';
import type {BulkOutcome} from '@/shared/api/bulk';
import type {
  CandidateStrength,
  PagedPersonnel,
  PersonnelCandidate,
  PersonnelInput,
  PersonnelInvite,
  PersonnelRecord,
} from '@/entities/personnel/types';

export type FetchPersonnelParams = {
  search?: string;
  page?: number;
  perPage?: number;
  /** `name` | `employee_number` | `created_at` | `updated_at` — sv_control's own set. */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

export function fetchPersonnelList(
  companyId: string,
  params: FetchPersonnelParams = {},
): Promise<PagedPersonnel> {
  const qs = new URLSearchParams();
  if (params.search) {
    qs.set('search', params.search);
  }
  if (params.page !== undefined) {
    qs.set('page', String(params.page));
  }
  if (params.perPage !== undefined) {
    qs.set('per_page', String(params.perPage));
  }
  if (params.sortBy) {
    qs.set('sort_by', params.sortBy);
  }
  if (params.sortOrder) {
    qs.set('sort_order', params.sortOrder);
  }
  const query = qs.toString();
  return requestControl<PagedPersonnel>(
    query ? `/control/personnel?${query}` : '/control/personnel',
    companyId,
  );
}

/** One person WITH avatar_base64 hydrated. */
export function fetchPersonnel(
  companyId: string,
  recordId: string,
): Promise<PersonnelRecord> {
  return requestControl<PersonnelRecord>(
    `/control/personnel/${encodeURIComponent(recordId)}`,
    companyId,
  );
}

/** Create or update (UPSERT keyed by input.record_id — empty ⇒ create). */
export function savePersonnel(
  companyId: string,
  input: PersonnelInput,
): Promise<PersonnelRecord> {
  return requestControl<PersonnelRecord>('/control/personnel', companyId, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Who this company already knows by the traits being typed — `GET
 * /control/personnel/candidates`.
 *
 * Ask BEFORE enrolling. Under the same traits there may be a detached record (the same
 * person coming back, whose history and id should be reused) or an ACTIVE one, in
 * which case enrolling would put two rows under one human and one employee number.
 *
 * The search is locked to this company on the server. A global one would tell company
 * A that a person exists at company B, so there is no parameter to widen it.
 *
 * With neither `fullName` nor `employeeNumber` the server answers with an empty list
 * rather than the whole company — we do not call at all in that case, to save the trip.
 *
 * ## Casing
 *
 * Read through a normaliser rather than cast. Every other `/control/*` payload this
 * app parses is snake_case, but both the changelog and the live operation description
 * write this response's fields in camelCase (`recordId`, `detachedAt`, `strength`),
 * and the spec publishes no schema for the 200 to settle it. Accepting either shape
 * costs a few lines and cannot be wrong; guessing has a fifty-fifty chance of an empty
 * list that looks exactly like "nobody matched" — which is the one answer that must
 * never be produced by accident, because it is the answer that creates the duplicate.
 */
export type CandidateQuery = {
  fullName?: string;
  /** Only meaningful together with `fullName`. */
  birthDate?: string;
  employeeNumber?: string;
};

type CandidateWire = Record<string, unknown>;

function str(row: CandidateWire, ...keys: string[]): string {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === 'string' && v.length > 0) {
      return v;
    }
  }
  return '';
}

function toCandidate(row: CandidateWire): PersonnelCandidate {
  const detachedAt = str(row, 'detached_at', 'detachedAt');
  return {
    record_id: str(row, 'record_id', 'recordId'),
    full_name: str(row, 'full_name', 'fullName'),
    birth_date: str(row, 'birth_date', 'birthDate'),
    employee_number: str(row, 'employee_number', 'employeeNumber'),
    position: str(row, 'position'),
    detached_at: detachedAt || null,
    detached_by: str(row, 'detached_by', 'detachedBy') || null,
    // Anything the server has not taught us about is treated as WEAK: the default has
    // to be the one that refuses to preselect. See `CandidateStrength`.
    strength: (str(row, 'strength') === 'strong'
      ? 'strong'
      : 'weak') as CandidateStrength,
  };
}

export async function fetchPersonnelCandidates(
  companyId: string,
  query: CandidateQuery,
): Promise<PersonnelCandidate[]> {
  const fullName = query.fullName?.trim() ?? '';
  const employeeNumber = query.employeeNumber?.trim() ?? '';
  if (!fullName && !employeeNumber) {
    return [];
  }
  const qs = new URLSearchParams();
  if (fullName) {
    qs.set('fullName', fullName);
  }
  // The server only reads it alongside a name, so sending it alone would be noise.
  if (fullName && query.birthDate?.trim()) {
    qs.set('birthDate', query.birthDate.trim());
  }
  if (employeeNumber) {
    qs.set('employeeNumber', employeeNumber);
  }
  const res = await requestControl<
    CandidateWire[] | {data?: CandidateWire[]; candidates?: CandidateWire[]}
  >(`/control/personnel/candidates?${qs.toString()}`, companyId);
  // `data` first: the gateway re-wraps `/control/*` lists in its standard {meta, data},
  // which is exactly how the profiles picker once drew an empty list over a company
  // that had presets (see entities/device/api.ts). The changelog names the array
  // `candidates`; a bare array is what its own verification curl implies.
  const rows = Array.isArray(res) ? res : (res?.data ?? res?.candidates ?? []);
  return rows.map(toCandidate).filter(c => c.record_id.length > 0);
}

/**
 * DETACH, not delete — the call is unchanged but what it does is not (2026-08-24).
 *
 * The row leaves the catalogue, so the person stops being pushed to terminals and
 * stops opening doors, and their face is erased. The IDENTITY is kept — name, birth
 * date, employee number, position, and who detached them when — so that somebody
 * coming back is recognised by `fetchPersonnelCandidates` instead of being enrolled
 * twice. Saving under the same `record_id` puts them back and clears the detachment.
 *
 * Every label pointed at this call has to say so: offering it as "delete" promises an
 * erasure that does not happen and hides the way back.
 */
export function detachPersonnel(
  companyId: string,
  recordId: string,
): Promise<void> {
  return requestControl<void>(
    `/control/personnel/${encodeURIComponent(recordId)}`,
    companyId,
    {method: 'DELETE'},
  );
}

/** Mint a one-time code the employee redeems from the app (POST /me/link). */
export function createPersonnelInvite(
  companyId: string,
  recordId: string,
): Promise<PersonnelInvite> {
  return requestControl<PersonnelInvite>(
    `/control/personnel/${encodeURIComponent(recordId)}/invite`,
    companyId,
    {method: 'POST'},
  );
}

/**
 * Cut an employee's self-service link to their own record. The person stays on the
 * roster and keeps their face; only their ability to open /me/* against this company
 * goes away. The inverse of the invite above.
 */
export function unlinkPersonnelAccount(
  companyId: string,
  recordId: string,
): Promise<void> {
  return requestControl<void>(
    `/control/personnel/${encodeURIComponent(recordId)}/link`,
    companyId,
    {method: 'DELETE'},
  );
}

/**
 * ── Bulk operations ──────────────────────────────────────────────────────────
 *
 * All three take a list of record ids under the field name `ids` and answer 200 with a
 * `BulkOutcome` EVEN WHEN SOME ITEMS FAILED — `failed[]` names which and why (404
 * unknown, 409 ambiguous, 403 out of scope). A screen must therefore report partial
 * success as partial; reading a 200 as "all of them" is exactly the mistake this
 * envelope exists to prevent.
 *
 * At most 500 ids per call, counted BEFORE duplicates are removed. Past that the whole
 * request is a 400 and nothing is applied — hence `BULK_LIMIT`, which the callers
 * chunk against rather than discovering server-side.
 */
/**
 * The group form of {@link detachPersonnel}, and it detaches for the same reason —
 * the route name still says `delete`, what it does since 2026-08-24 does not.
 */
export function bulkDetachPersonnel(
  companyId: string,
  ids: string[],
): Promise<BulkOutcome> {
  return requestControl<BulkOutcome>('/control/personnel/bulk/delete', companyId, {
    method: 'POST',
    body: JSON.stringify({ids}),
  });
}

/**
 * Replaces (not merges) each person's department list. An explicitly empty array
 * clears every link — that is legitimate and accepted, unlike an empty `ids`.
 */
export function bulkSetPersonnelDepartments(
  companyId: string,
  ids: string[],
  departmentIds: string[],
): Promise<BulkOutcome> {
  return requestControl<BulkOutcome>('/control/personnel/bulk/department', companyId, {
    method: 'POST',
    body: JSON.stringify({ids, department_ids: departmentIds}),
  });
}

/**
 * Sets the access deadline. `0` is the wire's "unlimited" — the same sentinel
 * `PersonnelRecord.access_until_millis` uses, so clearing a deadline in bulk is
 * passing 0, not null. The server deliberately refuses to default this field for
 * exactly that reason: a missing value would grant 500 people unlimited access.
 */
export function bulkSetAccessUntil(
  companyId: string,
  ids: string[],
  accessUntilMillis: number,
): Promise<BulkOutcome> {
  return requestControl<BulkOutcome>('/control/personnel/bulk/access-until', companyId, {
    method: 'POST',
    body: JSON.stringify({ids, access_until_millis: accessUntilMillis}),
  });
}

/**
 * ── Roster import / export ───────────────────────────────────────────────────
 *
 * `template: true` asks for the BLANK spreadsheet instead of the current roster —
 * the same columns, no rows. That is the file the import below expects back, and
 * handing someone an empty roster to fill in beats documenting a column order.
 */
export function exportPersonnel(
  companyId: string,
  options: {format?: 'xlsx' | 'csv'; template?: boolean; lang?: string} = {},
): Promise<Response> {
  const qs = new URLSearchParams({format: options.format ?? 'xlsx'});
  if (options.template) {
    qs.set('template', 'true');
  }
  // Заголовки и лист с правилами приходят на том языке, на котором кабинет
  // сейчас говорит с человеком: заполнять файл будет он же. Незнакомый язык
  // сервер читает как русский, поэтому подставлять запасной не нужно.
  if (options.lang) {
    qs.set('lang', options.lang);
  }
  return requestControlRaw(`/control/personnel/export?${qs.toString()}`, companyId);
}

/**
 * Bulk-create people from a filled-in spreadsheet.
 *
 * Multipart, so the JSON content type the client sets by default has to be UNSET
 * (the empty string does that — see requestControlRaw's note) and the boundary left
 * for the runtime to write: a hand-written `multipart/form-data` header without the
 * generated boundary is a body the server cannot parse.
 */
/**
 * Итог загрузки файла — НЕ `BulkOutcome`.
 *
 * Здесь нет списка идентификаторов: строки файла ещё не записи, у них нет id,
 * и назвать их сервер может только номером строки. Раньше ответ был
 * типизирован как пакетная операция, и экран читал `succeeded.length` у
 * значения, которого в ответе нет вовсе.
 *
 * Поля в snake_case: так их отдаёт шлюз.
 */
export type PersonnelImportResult = {
  imported_count: number;
  skipped_count: number;
  issues: {row_number: number; message: string}[];
};

export function importPersonnel(
  companyId: string,
  file: ImportFile,
  lang?: string,
): Promise<PersonnelImportResult> {
  const form = new FormData();
  // Two runtimes, one call. On web the file picker hands over a real `File`, which
  // already carries its own name and MIME type. On native there is no File object at
  // all — React Native's FormData accepts the {uri, name, type} triple instead and
  // streams the file off disk itself. Neither is assignable to the other's type, and
  // RN's own FormData signature takes two arguments (no third `filename`), so the cast
  // is where the two worlds are joined.
  form.append('file', file as unknown as string);

  // Замечания по строкам приходят на языке кабинета (SV-60), включая те, что
  // кабинет сам не знает (например, про фото в файле). Без языка сервер
  // отвечает прежними английскими строками.
  const path = lang
    ? `/control/personnel/import?${new URLSearchParams({lang}).toString()}`
    : '/control/personnel/import';
  return requestControl<PersonnelImportResult>(path, companyId, {
    method: 'POST',
    // Unset (see requestControlRaw's note): the multipart boundary is generated by the
    // runtime, and a Content-Type written by hand would not contain it.
    headers: {'Content-Type': ''},
    body: form as unknown as RequestInit['body'],
  });
}

/** A web `File`, or the {uri, name, type} triple React Native's FormData understands. */
export type ImportFile = {uri: string; name: string; type: string} | Blob;

/**
 * ── Откреплённые (B3/B4) ─────────────────────────────────────────────────────
 *
 * Откреплённый — тот, кого убрали из штата: в дверь не пускают, лица нет, а
 * личность (имя, дата рождения, номер, должность, отделы, график) хранится год.
 * Отсюда его можно вернуть или удалить насовсем; через год без решения он
 * удаляется сам.
 */
export type DetachedPerson = {
  record_id: string;
  full_name: string;
  birth_date: string;
  employee_number: string;
  position: string;
  /** RFC 3339; пусто — не известно. */
  detached_at: string;
  detached_by: string;
};

export type DetachedPage = {items: DetachedPerson[]; total: number};

/** Сколько дней хранится откреплённый — то же число, что у ночной чистки. */
export const DETACHED_RETENTION_DAYS = 365;

export function fetchDetachedPersonnel(
  companyId: string,
  search: string,
  page: number,
  limit = 50,
): Promise<DetachedPage> {
  const q = new URLSearchParams({search, page: String(page), limit: String(limit)});
  return requestControl<Partial<DetachedPage>>(
    `/control/personnel/detached?${q.toString()}`,
    companyId,
  ).then(r => ({items: r.items ?? [], total: Number(r.total ?? 0)}));
}

/** Вернуть в штат: без отделов — в прежние. Лицо заводят заново. */
export function returnDetachedPersonnel(
  companyId: string,
  recordId: string,
  departmentIds: string[] = [],
): Promise<PersonnelRecord> {
  return requestControl<PersonnelRecord>(
    `/control/personnel/detached/${encodeURIComponent(recordId)}/return`,
    companyId,
    {method: 'POST', body: JSON.stringify({departmentIds})},
  );
}

/**
 * Удалить насовсем: личность, ставки, приглашения, подписки телеграма. Проходы
 * в журнале остаются без имени и номера. Отменить нельзя.
 */
export function purgeDetachedPersonnel(companyId: string, recordId: string): Promise<void> {
  return requestControl<void>(
    `/control/personnel/detached/${encodeURIComponent(recordId)}`,
    companyId,
    {method: 'DELETE'},
  );
}
